import { useEffect, useState } from "react";
import api from "@/lib/api";

// Live strength meter that fetches the current policy from the backend and
// evaluates the entered password client-side against it. Also indicates strength.
export default function PasswordStrengthMeter({ value, onValidityChange, className = "" }) {
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/auth/password-policy").then(r => { if (alive) setPolicy(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const pwd = value || "";
  const rules = policy ? [
    { key: "min_length", label: `At least ${policy.min_length} characters`, ok: pwd.length >= policy.min_length },
    ...(policy.require_upper ? [{ key: "upper", label: "An uppercase letter", ok: /[A-Z]/.test(pwd) }] : []),
    ...(policy.require_lower ? [{ key: "lower", label: "A lowercase letter", ok: /[a-z]/.test(pwd) }] : []),
    ...(policy.require_number ? [{ key: "number", label: "A digit", ok: /[0-9]/.test(pwd) }] : []),
    ...(policy.require_special ? [{ key: "special", label: "A special character", ok: /[^A-Za-z0-9]/.test(pwd) }] : []),
  ] : [];

  const allOk = rules.length > 0 && rules.every(r => r.ok);

  // Strength score based on length + variety
  const varietyScore = ([/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(rx => rx.test(pwd)).length);
  const lenScore = pwd.length >= 16 ? 3 : pwd.length >= 12 ? 2 : pwd.length >= 8 ? 1 : 0;
  const total = Math.min(4, varietyScore + lenScore - 1);
  const strength = pwd.length === 0 ? -1 : Math.max(0, total);
  const labels = ["Too weak", "Weak", "Fair", "Good", "Strong"];
  const colors = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-lime-500", "bg-green-500"];

  useEffect(() => {
    if (onValidityChange) onValidityChange(allOk);
  }, [allOk, onValidityChange]);

  if (!policy) return null;

  return (
    <div className={`mt-2 ${className}`}>
      <div className="h-1.5 bg-gray-200 flex overflow-hidden" aria-hidden="true">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className={`flex-1 ${i <= strength ? colors[strength] : ""}`} />
        ))}
      </div>
      {pwd.length > 0 && (
        <div className="mt-1 text-xs text-muted-foreground" data-testid="pw-strength">Strength: <b>{labels[strength >= 0 ? strength : 0]}</b></div>
      )}
      <ul className="mt-2 text-xs space-y-0.5">
        {rules.map(r => (
          <li key={r.key} className={r.ok ? "text-green-700" : "text-muted-foreground"} data-testid={`pw-rule-${r.key}`}>
            <span className="inline-block w-4">{r.ok ? "✓" : "○"}</span> {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
